import { useEffect, useRef } from "react";

// Marks a dialog (a drawer is one) whose keys the shortcuts may use.
const SCOPE_ATTRIBUTE = "data-keyboard-shortcuts";
export const SHORTCUT_SCOPE = { [SCOPE_ATTRIBUTE]: "" };

// Where a key belongs to something else: typing, or a menu/dialog's own keys.
const OWN_KEYS = [
  "input",
  "textarea",
  "select",
  '[contenteditable=""]',
  '[contenteditable="true"]',
  '[role="dialog"]',
  '[role="menu"]',
  '[role="listbox"]',
].join(", ");

/**
 * Single-key shortcuts (`"p"`, `"ArrowDown"`) on the whole window. Keys with a
 * modifier never match, so they are the same on every platform and leave the
 * browser's and OS's own shortcuts alone. Letters match whatever the layout
 * prints (caps lock or not). Inside a dialog they only work if it carries
 * `SHORTCUT_SCOPE`: an unmarked one over it (a confirmation) keeps its keys.
 */
export function useKeyboardShortcuts(shortcuts: Record<string, () => void>, enabled = true) {
  // The latest handlers, without re-adding the listener every render.
  const ref = useRef(shortcuts);
  ref.current = shortcuts;

  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.repeat || e.isComposing) return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      const owner = e.target instanceof Element ? e.target.closest(OWN_KEYS) : null;
      if (owner && !owner.hasAttribute(SCOPE_ATTRIBUTE)) return;
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      const handler = ref.current[key];
      if (!handler) return;
      e.preventDefault();
      handler();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled]);
}
